/**
 * The pivot builder (FR-PIL-07): a user crosses a row dimension, optionally a column dimension,
 * with measures over the expéditions picked up in a period. Pure aggregation over normalized
 * facts; the API loads the facts and caps the cells (Technical Specifications 11.3: 50,000).
 */
import { z } from 'zod';
import { round3 } from './expedition.js';
import { TREATMENT_FAMILY_LABELS, treatmentFamilyOf } from './indicators.js';
import { COST_KIND_LABELS, monthLabel, UNASSIGNED, WASTE_TYPE_LABELS } from './analyses.js';
export const PIVOT_DIMENSIONS = [
    'month',
    'quarter',
    'year',
    'company',
    'site',
    'origin',
    'waste',
    'chapter',
    'type',
    'transporter',
    'destination',
    'family',
    'conditionnement',
];
export const PIVOT_MEASURES = [
    'producerT',
    'receiverT',
    'count',
    'costTotal',
    'costTransport',
    'costTraitement',
    'costTaxe',
    'costOther',
    'revenue',
    'requestToPickupDays',
    'pickupToReturnDays',
];
/** Measures that read the cost lines, for the roles that read costs (FS 4.3). */
export const COST_MEASURES = [
    'costTotal',
    'costTransport',
    'costTraitement',
    'costTaxe',
    'costOther',
    'revenue',
];
export const PIVOT_CELL_LIMIT = 50_000;
export const pivotQuerySchema = z
    .object({
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    rows: z.enum(PIVOT_DIMENSIONS),
    columns: z.enum(PIVOT_DIMENSIONS).optional(),
    measures: z.array(z.enum(PIVOT_MEASURES)).min(1).max(4),
})
    .refine((q) => q.from <= q.to, { message: 'The period ends before it starts', path: ['to'] })
    .refine((q) => q.rows !== q.columns, {
    message: 'Rows and columns must differ',
    path: ['columns'],
});
export const DIMENSION_LABELS = {
    month: 'Mois',
    quarter: 'Trimestre',
    year: 'Année',
    company: 'Société',
    site: 'Site',
    origin: 'Origine',
    waste: 'Déchet',
    chapter: 'Chapitre du catalogue',
    type: 'Type de déchet',
    transporter: 'Transporteur',
    destination: 'Destinataire',
    family: 'Famille de traitement',
    conditionnement: 'Conditionnement',
};
export const MEASURE_LABELS = {
    producerT: { label: 'Tonnage producteur (t)', unit: 't' },
    receiverT: { label: 'Tonnage receveur (t)', unit: 't' },
    count: { label: 'Expéditions', unit: 'count' },
    costTotal: { label: 'Coût total (MAD)', unit: 'MAD' },
    costTransport: { label: COST_KIND_LABELS.TRANSPORT + ' (MAD)', unit: 'MAD' },
    costTraitement: { label: COST_KIND_LABELS.TRAITEMENT + ' (MAD)', unit: 'MAD' },
    costTaxe: { label: COST_KIND_LABELS.TAXE + ' (MAD)', unit: 'MAD' },
    costOther: { label: 'Autres coûts (MAD)', unit: 'MAD' },
    revenue: { label: COST_KIND_LABELS.RECETTE + ' (MAD)', unit: 'MAD' },
    requestToPickupDays: { label: 'Demande → enlèvement (jours)', unit: 'days' },
    pickupToReturnDays: { label: 'Enlèvement → retour (jours)', unit: 'days' },
};
function keyOf(f, dim) {
    const m = f.shippedAt.slice(0, 7);
    switch (dim) {
        case 'month':
            return { key: m, label: monthLabel(m) };
        case 'quarter': {
            const q = Math.ceil(Number(m.slice(5, 7)) / 3);
            return { key: `${m.slice(0, 4)}-T${q}`, label: `T${q} ${m.slice(0, 4)}` };
        }
        case 'year':
            return { key: m.slice(0, 4), label: m.slice(0, 4) };
        case 'company':
            return { key: f.companyName, label: f.companyName };
        case 'site':
            return { key: f.siteName, label: f.siteName };
        case 'origin':
            return { key: f.originName ?? '', label: f.originName ?? UNASSIGNED };
        case 'waste':
            return { key: `${f.wasteName}|${f.cmdCode}`, label: `${f.wasteName} (${f.cmdCode})` };
        case 'chapter': {
            const ch = f.cmdCode.replace(/\s/g, '').slice(0, 2);
            return { key: ch, label: `Chapitre ${ch}` };
        }
        case 'type':
            return { key: f.wasteType ?? '', label: WASTE_TYPE_LABELS[f.wasteType ?? ''] ?? UNASSIGNED };
        case 'transporter':
            return { key: f.transporterName ?? '', label: f.transporterName ?? UNASSIGNED };
        case 'destination':
            return { key: f.destinationName ?? '', label: f.destinationName ?? UNASSIGNED };
        case 'family': {
            const fam = treatmentFamilyOf(f.drCode);
            return { key: fam, label: TREATMENT_FAMILY_LABELS[fam].fr };
        }
        case 'conditionnement':
            return { key: f.conditionnementName ?? '', label: f.conditionnementName ?? UNASSIGNED };
    }
}
const days = (a, b) => Math.round((Date.parse(b.slice(0, 10)) - Date.parse(a.slice(0, 10))) / 86_400_000);
const OTHER_KINDS = [
    'ACHAT_CONDITIONNEMENT',
    'AUTRE_OPERATION',
    'LOCATION_CONDITIONNEMENT',
    'FRAIS_ANNEXES',
];
const acc = () => ({
    producerT: 0,
    receiverT: 0,
    count: 0,
    costTotal: 0,
    costTransport: 0,
    costTraitement: 0,
    costTaxe: 0,
    costOther: 0,
    revenue: 0,
    requestToPickup: { sum: 0, n: 0 },
    pickupToReturn: { sum: 0, n: 0 },
});
function add(a, f) {
    a.producerT += f.quantityT;
    a.receiverT += f.quantityReceivedT ?? 0;
    a.count += 1;
    const c = f.costs;
    const total = Object.values(c).reduce((s, v) => s + (v ?? 0), 0);
    a.costTotal += total;
    a.costTransport += c.TRANSPORT ?? 0;
    a.costTraitement += c.TRAITEMENT ?? 0;
    a.costTaxe += c.TAXE ?? 0;
    a.costOther += OTHER_KINDS.reduce((s, k) => s + (c[k] ?? 0), 0);
    a.revenue += c.RECETTE ?? 0;
    if (f.requestedAt) {
        a.requestToPickup.sum += days(f.requestedAt, f.shippedAt);
        a.requestToPickup.n += 1;
    }
    if (f.receivedAt) {
        a.pickupToReturn.sum += days(f.shippedAt, f.receivedAt);
        a.pickupToReturn.n += 1;
    }
}
const round2 = (n) => Math.round(n * 100) / 100;
function read(a, m) {
    switch (m) {
        case 'producerT':
            return round3(a.producerT);
        case 'receiverT':
            return round3(a.receiverT);
        case 'count':
            return a.count;
        case 'costTotal':
            return round2(a.costTotal);
        case 'costTransport':
            return round2(a.costTransport);
        case 'costTraitement':
            return round2(a.costTraitement);
        case 'costTaxe':
            return round2(a.costTaxe);
        case 'costOther':
            return round2(a.costOther);
        case 'revenue':
            return round2(a.revenue);
        case 'requestToPickupDays':
            return a.requestToPickup.n ? round2(a.requestToPickup.sum / a.requestToPickup.n) : null;
        case 'pickupToReturnDays':
            return a.pickupToReturn.n ? round2(a.pickupToReturn.sum / a.pickupToReturn.n) : null;
    }
}
const sortKeys = (keys, dim, weight) => ['month', 'quarter', 'year'].includes(dim)
    ? [...keys].sort((a, b) => a.key.localeCompare(b.key))
    : [...keys].sort((a, b) => weight(b.key) - weight(a.key) || a.label.localeCompare(b.label, 'fr'));
/** Crosses the facts: rows by `query.rows`, columns by `query.columns`, each cell holding the measures. */
export function pivot(facts, query, limit = PIVOT_CELL_LIMIT) {
    const rowKeys = new Map();
    const colKeys = new Map();
    const cells = new Map();
    const rowTotals = new Map();
    const colTotals = new Map();
    const grand = acc();
    const NONE = { key: '_', label: '' };
    for (const f of facts) {
        const r = keyOf(f, query.rows);
        const c = query.columns ? keyOf(f, query.columns) : NONE;
        rowKeys.set(r.key, r);
        colKeys.set(c.key, c);
        const id = `${r.key}\u0000${c.key}`;
        const cell = cells.get(id) ?? acc();
        add(cell, f);
        cells.set(id, cell);
        const rt = rowTotals.get(r.key) ?? acc();
        add(rt, f);
        rowTotals.set(r.key, rt);
        const ct = colTotals.get(c.key) ?? acc();
        add(ct, f);
        colTotals.set(c.key, ct);
        add(grand, f);
    }
    const values = (a) => Object.fromEntries(query.measures.map((m) => [m, a ? read(a, m) : null]));
    const weightRow = (k) => rowTotals.get(k)?.count ?? 0;
    const weightCol = (k) => colTotals.get(k)?.count ?? 0;
    let orderedRows = sortKeys([...rowKeys.values()], query.rows, weightRow);
    const orderedCols = query.columns
        ? sortKeys([...colKeys.values()], query.columns, weightCol)
        : [NONE];
    // the cell limit folds the smallest rows into one
    let truncated = false;
    const maxRows = Math.max(1, Math.floor(limit / Math.max(1, orderedCols.length)));
    if (orderedRows.length > maxRows) {
        truncated = true;
        const kept = orderedRows.slice(0, maxRows - 1);
        const folded = orderedRows.slice(maxRows - 1);
        const other = { key: '__other__', label: 'Autres' };
        const ot = acc();
        for (const r of folded) {
            for (const c of orderedCols) {
                const a = cells.get(`${r.key}\u0000${c.key}`);
                if (!a)
                    continue;
                const target = cells.get(`${other.key}\u0000${c.key}`) ?? acc();
                mergeAcc(target, a);
                cells.set(`${other.key}\u0000${c.key}`, target);
            }
            mergeAcc(ot, rowTotals.get(r.key));
        }
        rowTotals.set(other.key, ot);
        orderedRows = [...kept, other];
    }
    return {
        query,
        rows: orderedRows.map((r) => ({
            ...r,
            cells: Object.fromEntries(orderedCols.map((c) => [c.key, values(cells.get(`${r.key}\u0000${c.key}`))])),
            total: values(rowTotals.get(r.key)),
        })),
        columns: orderedCols,
        columnTotals: Object.fromEntries(orderedCols.map((c) => [c.key, values(colTotals.get(c.key))])),
        grandTotal: values(grand),
        facts: facts.length,
        cells: orderedRows.length * orderedCols.length,
        truncated,
    };
}
function mergeAcc(into, from) {
    into.producerT += from.producerT;
    into.receiverT += from.receiverT;
    into.count += from.count;
    into.costTotal += from.costTotal;
    into.costTransport += from.costTransport;
    into.costTraitement += from.costTraitement;
    into.costTaxe += from.costTaxe;
    into.costOther += from.costOther;
    into.revenue += from.revenue;
    into.requestToPickup.sum += from.requestToPickup.sum;
    into.requestToPickup.n += from.requestToPickup.n;
    into.pickupToReturn.sum += from.pickupToReturn.sum;
    into.pickupToReturn.n += from.pickupToReturn.n;
}
//# sourceMappingURL=pivot.js.map